import React, { useState, useEffect, useCallback } from 'react';
import formaPagamentoService from '../services/formaPagamentoService';
import api from '../services/api';

/**
 * Componente para marcar pedido como pago - Funcionalidade administrativa
 * Baseado nas regras especificadas no plano de implementação
 */
const PagamentoModal = ({ 
  isOpen, 
  onClose, 
  pedido, 
  onPaymentSuccess 
}) => {
  const [formData, setFormData] = useState({
    formaPagamento: '',
    dataRecebimento: new Date().toISOString().split('T')[0],
    pago: true
  });
  
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Carregar formas de pagamento
  const loadFormasPagamento = useCallback(async () => {
    try {
      setLoading(true);
      
      const result = await formaPagamentoService.getAll();
      console.log('Formas pagamento carregadas:', result);
      setFormasPagamento(result.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar formas de pagamento:', error);
      setError('Erro ao carregar formas de pagamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadFormasPagamento();
    }
  }, [isOpen, loadFormasPagamento]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verificar se o pedido já está pago
    if (pedido?.pagoTexto === 'Sim') {
      setError('Este pedido já foi marcado como pago');
      return;
    }

    // Verificar se o pedido está em rascunho
    if (pedido?.status === 'Rascunho') {
      setError('Não é possível marcar como pago um pedido em rascunho. Finalize o pedido primeiro.');
      return;
    }
    
    // Validações
    if (!formData.formaPagamento) {
      setError('Selecione uma forma de pagamento');
      return;
    }
    
    if (!formData.dataRecebimento) {
      setError('Informe a data de recebimento');
      return;
    }

    setError(''); // Limpar erros anteriores

    try {
      setSubmitting(true);
      
      const response = await api.patch(`/pedidos/${pedido.idProduto}/receive-payment`, formData);
      
      if (response.data.success) {
        setSuccess('Pagamento recebido com sucesso!');
        onPaymentSuccess(response.data.data);
        
        // Fechar modal após sucesso
        setTimeout(() => {
          onClose();
          setSuccess('');
          // Reset form
          setFormData({
            formaPagamento: '',
            dataRecebimento: new Date().toISOString().split('T')[0],
            pago: true
          });
        }, 2000);
        
      } else {
        setError(response.data.message || 'Erro ao processar pagamento');
      }
      
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      setError(error.response?.data?.message || 'Erro interno do servidor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Receber Pagamento</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded">
          <p className="text-sm text-gray-600">
            <strong>Pedido:</strong> #{pedido?.idProduto}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Valor:</strong> R$ {pedido?.valor && !isNaN(pedido.valor) ? Number(pedido.valor).toFixed(2) : '0,00'}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Status:</strong> {pedido?.status}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Pago:</strong> {pedido?.pagoTexto || 'Não'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded">
            {success}
          </div>
        )}

        {(pedido?.pagoTexto === 'Sim' || pedido?.status === 'Rascunho') && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded">
            <strong>Atenção:</strong> 
            {pedido?.pagoTexto === 'Sim' ? ' Este pedido já foi marcado como pago.' : ''}
            {pedido?.status === 'Rascunho' ? ' Pedidos em rascunho não podem ser marcados como pagos.' : ''}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Forma de Pagamento *
            </label>
            <select
              name="formaPagamento"
              value={formData.formaPagamento}
              onChange={handleChange}
              required
              disabled={loading || submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione...</option>
              {formasPagamento.map(forma => (
                <option key={forma.idFormaPagamento} value={forma.idFormaPagamento}>
                  {forma.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Recebimento *
            </label>
            <input
              type="date"
              name="dataRecebimento"
              value={formData.dataRecebimento}
              onChange={handleChange}
              required
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="pago"
              checked={formData.pago}
              onChange={handleChange}
              disabled={submitting}
              className="mr-2"
            />
            <label className="text-sm text-gray-700">
              Confirmo que o pagamento foi recebido
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !formData.pago || pedido?.pagoTexto === 'Sim' || pedido?.status === 'Rascunho'}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {submitting ? 'Processando...' : 
               pedido?.pagoTexto === 'Sim' ? 'Já Pago' : 
               pedido?.status === 'Rascunho' ? 'Pedido em Rascunho' : 
               'Confirmar Pagamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PagamentoModal;
